import { type ChangeEvent, type ReactNode } from 'react'
import {
  countWords,
  MAX_WORDS,
  type UpdateFormValue,
  type WaitlistFormData
} from './types'

const leetCodeOptions = ['Easy', 'Medium', 'Hard']
const codeforcesOptions = [
  '800–1000',
  '1100–1400',
  '1500–1800',
  '1900–2000',
  '2000+'
]
const timeOptions = ['12–2 PM', '2–4 PM', '4–6 PM', '6–8 PM', '8–10 PM']
const frequencyOptions = [
  'Both days · Saturday & Sunday',
  'Once a week · Saturday',
  'Once a week · Sunday'
]

interface DetailsStepProps {
  formData: WaitlistFormData
  onChange: UpdateFormValue
}

export function DetailsStep({ formData, onChange }: DetailsStepProps) {
  return (
    <div className="details-page">
      <div className="question">
        <h2>Help us shape your class.</h2>
        <p>Two optional sections help us plan the right experience.</p>
      </div>
      <SignupSection
        label="01 / YOUR PROFILE"
        title="Tell us where you are."
        description="Your profiles and preferred problem level help us group classes well."
      >
        <TextField
          label="Your Competitive Programming profiles and max ratings"
          value={formData.user_profiles}
          placeholder="e.g. Codeforces: aryanc403 (1423), LeetCode: aryanc403 (1650)"
          onChange={(value) => onChange(value, 'user_profiles', false, true)}
        />
        <div className="detail-field">
          <label>Problems you’d like to cover</label>
          <OptionGroup
            title="LeetCode"
            options={leetCodeOptions}
            selected={formData.leetcode_difficulty}
            onSelect={(value) => onChange(value, 'leetcode_difficulty', true)}
          />
          <OptionGroup
            title="Codeforces"
            options={codeforcesOptions}
            selected={formData.codeforces_difficulty}
            onSelect={(value) => onChange(value, 'codeforces_difficulty', true)}
          />
        </div>
      </SignupSection>
      <SignupSection
        label="02 / CLASS SCHEDULE"
        title="Tell us what works for you."
        description="Your schedule and feedback guide how we run these classes."
      >
        <div className="detail-field">
          <label>Preferred time in IST</label>
          <OptionGrid
            options={timeOptions}
            selected={formData.lesson_time_ist}
            onSelect={(value) => onChange(value, 'lesson_time_ist', true)}
          />
        </div>
        <div className="detail-field">
          <label>Preferred lesson frequency</label>
          <OptionGrid
            options={frequencyOptions}
            selected={
              formData.lesson_frequency ? [formData.lesson_frequency] : []
            }
            onSelect={(value) => onChange(value, 'lesson_frequency')}
            showIndicator={false}
          />
        </div>
        <TextField
          label="Anything else we should know?"
          value={formData.notes}
          placeholder="Tell us what you want from a great competitive programming class…"
          onChange={(value) => onChange(value, 'notes', false, true)}
        />
      </SignupSection>
    </div>
  )
}

function SignupSection({
  label,
  title,
  description,
  children
}: {
  label: string
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <section className="signup-section">
      <div className="signup-section-heading">
        <span>{label}</span>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      {children}
    </section>
  )
}

function TextField({
  label,
  value,
  placeholder,
  onChange
}: {
  label: string
  value: string
  placeholder: string
  onChange: (value: string) => void
}) {
  return (
    <div className="detail-field">
      <label>{label}</label>
      <textarea
        value={value}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(event.target.value)
        }
        placeholder={placeholder}
      />
      <p className="word-count">
        {countWords(value)} / {MAX_WORDS} words
      </p>
    </div>
  )
}

function OptionGroup({
  title,
  options,
  selected,
  onSelect
}: {
  title: string
  options: string[]
  selected: string[]
  onSelect: (value: string) => void
}) {
  return (
    <div className="option-group">
      <p>{title}</p>
      <OptionGrid options={options} selected={selected} onSelect={onSelect} />
    </div>
  )
}

function OptionGrid({
  options,
  selected,
  onSelect,
  showIndicator = true
}: {
  options: string[]
  selected: string[]
  onSelect: (value: string) => void
  showIndicator?: boolean
}) {
  return (
    <div className="option-grid">
      {options.map((option) => (
        <button
          className={selected.includes(option) ? 'option selected' : 'option'}
          onClick={() => onSelect(option)}
          type="button"
          key={option}
        >
          {option}
          {showIndicator && (
            <span>{selected.includes(option) ? '✓' : '+'}</span>
          )}
        </button>
      ))}
    </div>
  )
}
